/* eslint-disable dot-notation */
import { JustifyContentProps, FlexDirectionProps } from '@altermaind/web/ui';
import { themeShared } from '../styles/theme';
import { useBreakingPoints } from './useBreakpoint';

// Add new type of values if you need others types (now we accept only: xs, s, ecc or column, row, ecc)
type LocalSpace = keyof typeof themeShared.space;
type LocalSizes = keyof typeof themeShared.sizes;
type LocalFlexDirection = FlexDirectionProps['flexDirection'];
type LocalFlexJustifyContent = JustifyContentProps['justifyContent'];

type PermitValue = LocalSpace | LocalFlexDirection | LocalFlexJustifyContent | LocalSizes | number;

type Breakpoints =
  | {
      mobile: PermitValue;
    }
  | {
      tablet: PermitValue;
    }
  | {
      desktop: PermitValue;
    }
  | {
      desktopLarge: PermitValue;
    }
  | { mobile: PermitValue; tablet: PermitValue }
  | { mobile: PermitValue; desktop: PermitValue }
  | { mobile: PermitValue; desktopLarge: PermitValue }
  | { tablet: PermitValue; desktop: PermitValue }
  | { tablet: PermitValue; desktopLarge: PermitValue }
  | { desktop: PermitValue; desktopLarge: PermitValue }
  | { mobile: PermitValue; tablet: PermitValue; desktop: PermitValue }
  | { mobile: PermitValue; tablet: PermitValue; desktopLarge: PermitValue }
  | { mobile: PermitValue; desktop: PermitValue; desktopLarge: PermitValue }
  | { tablet: PermitValue; desktop: PermitValue; desktopLarge: PermitValue }
  | { mobile: PermitValue; tablet: PermitValue; desktop: PermitValue; desktopLarge: PermitValue };

type Props<BP extends Breakpoints> =
  HasAllBreakpoints<BP> extends true
    ? { breakPoints: BP }
    : { breakPoints: BP } & { defaultValue: PermitValue };

type HasAllBreakpoints<BP extends Breakpoints> = BP extends {
  desktop: PermitValue;
  tablet: PermitValue;
  mobile: PermitValue;
}
  ? true
  : false;

/**
 * useResponsiveStyle
 * Hook to define the responsive style of your component.
 * @returns styleResponsive
 */
const useResponsiveStyle = () => {
  const { isMobile, isTablet, isDesktop, isDesktopLarge } = useBreakingPoints();

  /**
   * Breakpoints\
   * Desktop: 1200px - 1980px\
   * Tablet: 768px - 1199px\
   * Mobile: < 768px\
   * The breakpoints are defined in styles/theme/theme.ts
   * @returns styleResponsive
   */
  const styleResponsive = <BP extends Breakpoints>(props: Props<BP>) => {
    const { breakPoints } = props;

    // @ts-ignore
    const defaultValueInternal = props && props?.['defaultValue'];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const array: any[] = [
      // @ts-ignore
      (breakPoints && breakPoints?.['mobile']) || defaultValueInternal,
      // @ts-ignore
      (breakPoints && breakPoints?.['mobile']) || defaultValueInternal,
      // @ts-ignore
      (breakPoints && breakPoints?.['tablet']) || defaultValueInternal,
      // @ts-ignore
      (breakPoints && breakPoints?.['desktop']) || defaultValueInternal,
      // @ts-ignore
      (breakPoints && breakPoints?.['desktop']) || defaultValueInternal,
    ];
    return array;
  };

  const genericResponsiveValue = <T = string>(props: Props<Breakpoints>): T => {
    const { breakPoints } = props;

    // @ts-ignore
    if (isMobile && breakPoints?.['mobile']) {
      // @ts-ignore
      return breakPoints?.['mobile'];
    } // @ts-ignore
    if (isTablet && breakPoints?.['tablet']) {
      // @ts-ignore
      return breakPoints?.['tablet'];
    }
    // @ts-ignore
    if (isDesktopLarge && breakPoints?.['desktopLarge']) {
      // @ts-ignore
      return breakPoints?.['desktopLarge'];
    }
    // @ts-ignore
    if (isDesktop && breakPoints?.['desktop']) {
      // @ts-ignore
      return breakPoints?.['desktop'];
    }

    return props && (props?.['defaultValue'] as T);
  };

  return { styleResponsive, genericResponsiveValue };
};

export { useResponsiveStyle };
